import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Avatar,
    Divider,
    Tab,
    Tabs
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';

// icons
import {
    IconCloud,
    IconCloudUpload,
    IconSettings,
    IconCheck,
    IconX,
    IconPlus,
    IconRefresh,
    IconEdit,
    IconTrash,
    IconDatabase,
    IconFolder
} from '@tabler/icons-react';

const CloudStorageIntegration = () => {
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [connectionDialog, setConnectionDialog] = useState(false);
    const [syncDialog, setSyncDialog] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // Cloud storage providers
    const providers = [
        {
            id: 's3',
            name: 'Amazon S3',
            icon: '☁️',
            color: '#FF9900',
            status: 'connected',
            buckets: 5,
            usage: '45.2 GB',
            lastSync: '5 mins ago',
            credentials: {
                accessKeyId: 'AKIA••••••••',
                region: 'us-east-1',
                bucket: 'datahub-storage'
            }
        },
        {
            id: 'gcs',
            name: 'Google Cloud Storage',
            icon: '🔵',
            color: '#4285F4',
            status: 'disconnected',
            buckets: 0,
            usage: '0 GB',
            lastSync: 'Never'
        },
        {
            id: 'azure',
            name: 'Azure Blob Storage',
            icon: '🔷',
            color: '#0078D4',
            status: 'disconnected',
            buckets: 0,
            usage: '0 GB',
            lastSync: 'Never'
        },
        {
            id: 'minio',
            name: 'MinIO',
            icon: '🟥',
            color: '#C72E49',
            status: 'connected',
            buckets: 3,
            usage: '12.8 GB',
            lastSync: '1 hour ago',
            credentials: {
                endpoint: 'http://localhost:9000',
                accessKey: 'minioadmin',
                bucket: 'datahub-local'
            }
        }
    ];

    // Sync rules
    const syncRules = [
        {
            id: 1,
            name: 'Daily Backup to S3',
            source: 'Local Storage',
            destination: 'Amazon S3',
            schedule: 'Daily at 2:00 AM',
            status: 'active',
            lastRun: '2024-01-15 02:00',
            filesSync: 1247
        },
        {
            id: 2,
            name: 'Archive Old Files',
            source: 'Local Storage',
            destination: 'MinIO',
            schedule: 'Weekly on Sunday',
            status: 'active',
            lastRun: '2024-01-14 00:00',
            filesSync: 342
        }
    ];

    // Usage chart
    const usageChartOptions = {
        chart: {
            type: 'area',
            height: 250,
            toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
            categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        yaxis: {
            title: { text: 'Storage (GB)' },
            labels: { style: { colors: theme.palette.text.secondary } }
        },
        colors: ['#FF9900', '#C72E49', '#4285F4'],
        legend: { position: 'top' }
    };

    const usageChartSeries = [
        { name: 'Amazon S3', data: [30, 35, 38, 40, 42, 45] },
        { name: 'MinIO', data: [5, 7, 9, 10, 11, 13] },
        { name: 'Local', data: [20, 22, 25, 27, 28, 30] }
    ];

    const handleConnect = (provider) => {
        setSelectedProvider(provider);
        setConnectionDialog(true);
        setActiveStep(0);
    };

    const handleStepNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleStepBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const connectionSteps = ['Select Provider', 'Enter Credentials', 'Test Connection', 'Configure Settings'];

    return (
        <Box>
            {/* Header Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Connected Providers
                                    </Typography>
                                    <Typography variant="h3">2/4</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                                    <IconCloud />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Total Cloud Storage
                                    </Typography>
                                    <Typography variant="h3">58 GB</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light }}>
                                    <IconDatabase />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Active Sync Rules
                                    </Typography>
                                    <Typography variant="h3">2</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light }}>
                                    <IconRefresh />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                        Files Synced Today
                                    </Typography>
                                    <Typography variant="h3">347</Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light }}>
                                    <IconCloudUpload />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Content Tabs */}
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label="Providers" />
                        <Tab label="Sync Rules" />
                        <Tab label="Usage Analytics" />
                        <Tab label="Settings" />
                    </Tabs>
                </Box>

                {/* Providers Tab */}
                {tabValue === 0 && (
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h4">Cloud Storage Providers</Typography>
                            <Button variant="contained" startIcon={<IconPlus />} onClick={() => setConnectionDialog(true)}>
                                Add Provider
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            {providers.map((provider) => (
                                <Grid item xs={12} md={6} key={provider.id}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            border:
                                                provider.status === 'connected' ? `2px solid ${theme.palette.success.main}` : '1px solid',
                                            borderColor:
                                                provider.status === 'connected' ? theme.palette.success.main : theme.palette.divider
                                        }}
                                    >
                                        <CardContent>
                                            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <Typography variant="h2">{provider.icon}</Typography>
                                                    <Box>
                                                        <Typography variant="h5">{provider.name}</Typography>
                                                        <Chip
                                                            label={provider.status}
                                                            size="small"
                                                            color={provider.status === 'connected' ? 'success' : 'default'}
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <IconButton size="small">
                                                    <IconSettings />
                                                </IconButton>
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            {provider.status === 'connected' ? (
                                                <>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Buckets/Containers
                                                            </Typography>
                                                            <Typography variant="h6">{provider.buckets}</Typography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Storage Used
                                                            </Typography>
                                                            <Typography variant="h6">{provider.usage}</Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Last Sync
                                                            </Typography>
                                                            <Typography variant="body2">{provider.lastSync}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                    <Box display="flex" gap={1} mt={2}>
                                                        <Button size="small" startIcon={<IconCloudUpload />}>
                                                            Upload
                                                        </Button>
                                                        <Button size="small" startIcon={<IconFolder />}>
                                                            Browse
                                                        </Button>
                                                        <Button size="small" color="error" startIcon={<IconX />}>
                                                            Disconnect
                                                        </Button>
                                                    </Box>
                                                </>
                                            ) : (
                                                <Box textAlign="center" py={3}>
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        Not connected
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<IconPlus />}
                                                        onClick={() => handleConnect(provider)}
                                                    >
                                                        Connect {provider.name}
                                                    </Button>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                )}

                {/* Sync Rules Tab */}
                {tabValue === 1 && (
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h4">Sync Rules</Typography>
                            <Button variant="contained" startIcon={<IconPlus />} onClick={() => setSyncDialog(true)}>
                                Create Rule
                            </Button>
                        </Box>

                        <List>
                            {syncRules.map((rule, index) => (
                                <React.Fragment key={rule.id}>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                                                <IconRefresh />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle1">{rule.name}</Typography>
                                                    <Chip
                                                        label={rule.status}
                                                        size="small"
                                                        color={rule.status === 'active' ? 'success' : 'default'}
                                                    />
                                                </Box>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {rule.source} → {rule.destination}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Schedule: {rule.schedule} • Last run: {rule.lastRun} • Files synced:{' '}
                                                        {rule.filesSync}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton size="small">
                                                <IconEdit />
                                            </IconButton>
                                            <IconButton size="small" color="error">
                                                <IconTrash />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < syncRules.length - 1 && <Divider variant="inset" component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </CardContent>
                )}

                {/* Usage Analytics Tab */}
                {tabValue === 2 && (
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Cloud Storage Usage Analytics
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Storage Growth Trend
                                        </Typography>
                                        <ReactApexChart options={usageChartOptions} series={usageChartSeries} type="area" height={250} />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Cost Analysis
                                        </Typography>
                                        <List>
                                            <ListItem>
                                                <ListItemText primary="Amazon S3" secondary="Standard storage" />
                                                <Typography variant="h6">$12.45/mo</Typography>
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemText primary="MinIO" secondary="Self-hosted" />
                                                <Typography variant="h6">$0.00/mo</Typography>
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1">
                                                            <strong>Total Cost</strong>
                                                        </Typography>
                                                    }
                                                />
                                                <Typography variant="h5" color="primary">
                                                    $12.45/mo
                                                </Typography>
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h5" gutterBottom>
                                            Transfer Statistics
                                        </Typography>
                                        <List>
                                            <ListItem>
                                                <ListItemText primary="Upload Speed" secondary="Average" />
                                                <Typography variant="h6">45 MB/s</Typography>
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemText primary="Download Speed" secondary="Average" />
                                                <Typography variant="h6">68 MB/s</Typography>
                                            </ListItem>
                                            <Divider />
                                            <ListItem>
                                                <ListItemText primary="Total Transfer" secondary="This month" />
                                                <Typography variant="h6">2.4 TB</Typography>
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                )}
            </Card>

            {/* Connection Dialog */}
            <Dialog open={connectionDialog} onClose={() => setConnectionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Connect Cloud Storage Provider</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {connectionSteps.map((label, index) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                    <StepContent>
                                        {index === 0 && (
                                            <Box>
                                                <FormControl fullWidth sx={{ mb: 2 }}>
                                                    <InputLabel>Provider</InputLabel>
                                                    <Select value={selectedProvider?.id || ''} label="Provider">
                                                        <MenuItem value="s3">Amazon S3</MenuItem>
                                                        <MenuItem value="gcs">Google Cloud Storage</MenuItem>
                                                        <MenuItem value="azure">Azure Blob Storage</MenuItem>
                                                        <MenuItem value="minio">MinIO</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}
                                        {index === 1 && (
                                            <Box>
                                                <TextField fullWidth label="Access Key ID" variant="outlined" sx={{ mb: 2 }} />
                                                <TextField
                                                    fullWidth
                                                    label="Secret Access Key"
                                                    type="password"
                                                    variant="outlined"
                                                    sx={{ mb: 2 }}
                                                />
                                                <TextField fullWidth label="Region/Endpoint" variant="outlined" sx={{ mb: 2 }} />
                                            </Box>
                                        )}
                                        {index === 2 && (
                                            <Box textAlign="center" py={3}>
                                                <IconCheck size={48} color={theme.palette.success.main} />
                                                <Typography variant="h6" gutterBottom>
                                                    Connection Successful!
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Successfully connected to Amazon S3
                                                </Typography>
                                            </Box>
                                        )}
                                        {index === 3 && (
                                            <Box>
                                                <FormControlLabel
                                                    control={<Switch defaultChecked />}
                                                    label="Enable automatic sync"
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormControlLabel
                                                    control={<Switch defaultChecked />}
                                                    label="Encrypt files at rest"
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormControlLabel control={<Switch />} label="Enable versioning" />
                                            </Box>
                                        )}
                                        <Box sx={{ mb: 2, mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleStepNext}
                                                sx={{ mt: 1, mr: 1 }}
                                                disabled={index === connectionSteps.length - 1}
                                            >
                                                {index === connectionSteps.length - 1 ? 'Finish' : 'Continue'}
                                            </Button>
                                            <Button disabled={index === 0} onClick={handleStepBack} sx={{ mt: 1, mr: 1 }}>
                                                Back
                                            </Button>
                                        </Box>
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConnectionDialog(false)}>Cancel</Button>
                    {activeStep === connectionSteps.length - 1 && (
                        <Button variant="contained" onClick={() => setConnectionDialog(false)}>
                            Complete Setup
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CloudStorageIntegration;
